(() => {
  if (customElements.get('bnb-product-video')) return;
  let active = null;
  const competingOverlay = () => document.querySelector('basic-modal[open],drawer-component[open],quick-view-modal[open],modal-component[open],.pswp--open,dialog[open]:not(.bnb-pv__dialog)');
  class BnbProductVideo extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.trigger = this.querySelector('.bnb-pv__trigger');
      this.dialog = this.querySelector('dialog');
      this.template = this.querySelector('template');
      if (!this.trigger || !this.dialog || !this.template) return;
      this.stage = this.dialog.querySelector('.bnb-pv__stage');
      this.closeButton = this.dialog.querySelector('.bnb-pv__close');
      this.ratio = Math.max(0.1, Number(this.dataset.aspectRatio) || 0.5625);
      if (this.ratio > 1) this.dialog.setAttribute('data-landscape', '');
      this.trigger.addEventListener('click', () => this.open(), options);
      this.closeButton.addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.cleanup(); }, options);
      this.dialog.addEventListener('pointerdown', event => { this.backdropDown = this.isBackdrop(event); }, options);
      this.dialog.addEventListener('pointerup', event => { if (this.backdropDown && this.isBackdrop(event)) this.close(); this.backdropDown = false; }, options);
    }
    disconnectedCallback() {
      this.close();
      this.controller?.abort();
      this.controller = null;
      if (this.dialog) this.append(this.dialog);
    }
    isBackdrop(event) {
      const rect = this.dialog.getBoundingClientRect();
      return event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
    }
    open() {
      if (this.dialog.open || competingOverlay()) return;
      active?.close();
      active = this;
      document.body.append(this.dialog);
      this.scroll = { x: window.scrollX, y: window.scrollY };
      this.savedStyles = {};
      for (const key of ['position', 'top', 'left', 'width', 'overflow', 'padding-right']) {
        this.savedStyles[key] = [document.body.style.getPropertyValue(key), document.body.style.getPropertyPriority(key)];
      }
      const gutter = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const padding = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scroll.y}px`;
      document.body.style.left = `-${this.scroll.x}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${padding + gutter}px`;
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded', 'true');
      this.closeButton.focus({ preventScroll: true });
      this.session = new AbortController();
      const options = { signal: this.session.signal };
      this.video = this.template.content.querySelector('video').cloneNode(true);
      this.video.defaultMuted = true;
      this.video.muted = true;
      this.video.addEventListener('contextmenu', event => event.preventDefault(), options);
      this.video.tabIndex = 0;
      this.message('Loading video…');
      this.video.addEventListener('loadedmetadata', () => {
        if (this.video.videoWidth && this.video.videoHeight) {
          this.ratio = this.video.videoWidth / this.video.videoHeight;
          this.dialog.toggleAttribute('data-landscape', this.ratio > 1);
          this.stage.style.aspectRatio = this.ratio;
        }
        this.fit();
      }, options);
      this.video.addEventListener('waiting', () => this.message('Loading video…'), options);
      this.video.addEventListener('playing', () => this.message(''), options);
      this.video.addEventListener('canplay', () => { this.message(''); }, options);
      const failed = () => this.message('This video could not load. Please close it and try again.');
      this.video.addEventListener('error', failed, options);
      this.video.querySelectorAll('source').forEach(source => {
        source.addEventListener('error', failed, options);
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      this.stage.replaceChildren(this.video);
      this.fit();
      window.addEventListener('resize', () => this.fit(), options);
      window.visualViewport?.addEventListener('resize', () => this.fit(), options);
      this.resizeObserver = new ResizeObserver(() => this.fit());
      this.resizeObserver.observe(this.dialog.querySelector('.bnb-pv__header'));
      const notice = this.dialog.querySelector('.bnb-pv__notice');
      if (notice) this.resizeObserver.observe(notice);
      this.observer = new MutationObserver(() => { if (competingOverlay()) this.close(); });
      this.observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'class'] });
      this.video.load();
      const player = this.video;
      player.playbackRate = 1;
      player.play()?.catch(error => {
        if (!this.dialog.open || this.video !== player || error.name === 'AbortError') return;
        if (error.name === 'NotAllowedError') this.message('Tap Play to watch the video.');
        else failed();
      });
    }
    message() {}
    fit() {
      if (!this.dialog.open) return;
      const viewport = window.visualViewport;
      const cap = this.ratio > 1 ? 800 : window.innerWidth >= 750 ? 400 : 340;
      this.dialog.style.width = `${Math.min((viewport?.width || window.innerWidth) * 0.9, cap)}px`;
      this.dialog.style.maxHeight = '';
      const available = Math.min((viewport?.height || window.innerHeight) - 24, parseFloat(getComputedStyle(this.dialog).maxHeight) || window.innerHeight);
      this.dialog.style.maxHeight = `${available}px`;
      const overhead = this.dialog.querySelector('.bnb-pv__header').getBoundingClientRect().height + 24;
      const heightLimitedWidth = Math.max(160, (available - overhead) * this.ratio + 16);
      this.dialog.style.width = `${Math.min((viewport?.width || window.innerWidth) * 0.9, cap, heightLimitedWidth)}px`;
    }
    close() {
      if (this.dialog?.open) this.dialog.close();
      this.cleanup();
    }
    cleanup() {
      if (active !== this) return;
      active = null;
      this.session?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.video) {
        this.video.pause();
        try { this.video.currentTime = 0; } catch (_) {}
        this.video.removeAttribute('src');
        this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        this.video.load();
        this.video.remove();
        this.video = null;
      }
      this.dialog.style.width = '';
      this.dialog.style.maxHeight = '';
      for (const [key, [value, priority]] of Object.entries(this.savedStyles || {})) {
        if (value) document.body.style.setProperty(key, value, priority);
        else document.body.style.removeProperty(key);
      }
      const root = document.documentElement;
      const behavior = [root.style.getPropertyValue('scroll-behavior'), root.style.getPropertyPriority('scroll-behavior')];
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(this.scroll.x, this.scroll.y);
      if (behavior[0]) root.style.setProperty('scroll-behavior', ...behavior);
      else root.style.removeProperty('scroll-behavior');
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }
  }
  customElements.define('bnb-product-video', BnbProductVideo);
})();
